// src/components/study/StudyAudioPlayer.tsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface StudyAudioPlayerProps {
    audioUrl: string;
}

export const StudyAudioPlayer: React.FC<StudyAudioPlayerProps> = ({ audioUrl }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const rafRef = useRef<number>(0);                          // ← RAF handle
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);

    // ✅ Loop fluido a 60fps — substitui o timeupdate
    const tick = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        setCurrentTime(audio.currentTime);
        rafRef.current = requestAnimationFrame(tick);
    }, []);

    const startRaf = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
    }, [tick]);

    const stopRaf = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateDuration = () => setDuration(audio.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            stopRaf();
            setCurrentTime(0);
        };

        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('ended', handleEnded);
            cancelAnimationFrame(rafRef.current);              // ← cleanup no unmount
        };
    }, [stopRaf]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            stopRaf();                                         // ← para o loop
        } else {
            audio.play();
            startRaf();                                        // ← inicia o loop
        }
        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newVolume = parseFloat(e.target.value);
        audio.volume = newVolume;
        setVolume(newVolume);
        if (newVolume === 0) setIsMuted(true);
        else if (isMuted) setIsMuted(false);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = parseFloat(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    const cvAccentLight = getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim();
    const cvA15 = getComputedStyle(document.body).getPropertyValue('--platform-a15').trim();

    return (
        <div className="graia-audio-player-study">
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            <button
                className="study-play-btn"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
                {isPlaying ? (
                    <Pause size={20} />
                ) : (
                    <Play size={20} style={{ marginLeft: '2px' }} />
                )}
            </button>

            {/* Progresso */}
            <div className="study-progress-area">
                <span className="study-time">{formatTime(currentTime)}</span>

                <div className="study-track-wrapper">
                    {/* Barra visual customizada */}
                    <div className="study-track-bg">
                        <div
                            className="study-track-fill"
                            style={{ width: `${progress}%` }}
                        />
                        <div
                            className="study-track-thumb"
                            style={{ left: `${progress}%` }}
                        />
                    </div>
                    {/* Input invisível por cima para capturar interação */}
                    <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        step="0.01"
                        value={currentTime}
                        onChange={handleSeek}
                        className="study-progress-bar-hidden"
                    />
                </div>
            </div>

            <div className="study-volume-area">
                <button
                    className="study-volume-btn"
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Ativar som' : 'Silenciar'}
                >
                    {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="study-volume-slider"
                    style={{
                        background: `linear-gradient(to right, ${cvAccentLight} 0%, ${cvAccentLight} ${(isMuted ? 0 : volume) * 100}%, ${cvA15} ${(isMuted ? 0 : volume) * 100}%, ${cvA15} 100%)`
                    }}
                />
            </div>
        </div>
    );
};